import * as R from 'ramda'
import * as ReactRedux from 'react-redux'
import PropTypes from 'prop-types'
import React from 'react'

import * as I18n from '@rushplay/i18n'
import * as Common from '@rushplay/common'
import * as Forms from '@rushplay/forms'

import * as CombinedSelectors from '../combined-selectors'
import * as Countries from '../countries'
import { Logotype } from '../components/common/logotype'
import { QueryDrawer } from '../query-drawer'
import { RegistrationForm } from '../registration-form'
import { RegistrationStep } from '../constants'

import { ProgressBar } from './progress-bar'
import { dataSchema } from './data-schema'

export function RegistrationDrawer(props) {
  const i18n = I18n.useI18n()
  const [step, setStep] = React.useState(RegistrationStep.Credentials)
  const [codeSentTo, setCodeSentTo] = React.useState(null)
  const [errors, setErrors] = React.useState([])

  const dispatch = ReactRedux.useDispatch()
  const countries = ReactRedux.useSelector(state =>
    Countries.getAllCountries(state.countries)
  )
  const enabledCountryList = ReactRedux.useSelector(state =>
    Countries.getCountries(state.countries)
  )
  const locale = ReactRedux.useSelector(CombinedSelectors.getLocale)

  const countryNames = React.useMemo(() => {
    if (!countries || countries.length === 0) {
      return []
    }
    return countries
      .filter(country => country?.alpha2)
      .map(country => {
        const translatedName = i18n.translate(
          `register.country.${country.alpha2.toLowerCase()}`
        )
        return {
          alpha2: country.alpha2,
          translatedName: translatedName,
        }
      })
      .filter(country => country.translatedName)
      .sort((a, b) => {
        const nameA = a.translatedName
        const nameB = b.translatedName

        return nameA.localeCompare(nameB, locale, {
          numeric: true,
          sensitivity: 'base',
          ignorePunctuation: true,
        })
      })
      .map(country => country.alpha2)
  }, [countries, i18n.translate, locale])

  const enabledCountries = React.useMemo(
    () =>
      countries
        // DE is blocked and it is hardcoded on BE but still appears with enabled: true
        ?.filter(
          country =>
            country?.enabled && !country?.blocked && country?.alpha2 !== 'DE'
        )
        ?.map(country => country?.alpha2),
    [countries]
  )

  const callingCodeOptions = React.useMemo(() => {
    if (!countries || countries.length === 0) {
      return enabledCountryList ? enabledCountryList : []
    }

    const filtered = countries.filter(country => country?.countryCode != null)

    const result = [
      ...new Set(filtered.map(country => country.countryCode.toString())),
    ].sort((a, b) => a - b)

    return result
  }, [countries])

  const schema = React.useMemo(
    () =>
      dataSchema({
        countryCallingCode: callingCodeOptions,
        country: countryNames,
        enabledCountries: enabledCountries,
        maxYear: new Date(Date.now()).getFullYear() - 18,
        shouldVerifyPhone: props.shouldVerifyPhone,
      }),
    [
      props.shouldVerifyPhone,
      callingCodeOptions,
      enabledCountries,
      countryNames,
    ]
  )

  function handleSubmit(data) {
    const userData = {
      affiliateClickId: props.affiliateClickId,
      affiliateSubId: props.affiliateSubId,
      allowEmail: data.promotional,
      allowSms: data.promotional,
      birthdate: `${data.bdayDay}/${data.bdayMonth}/${data.bdayYear}`,
      city: data.city,
      clientType: props.clientType,
      countryCallingCode: `+${data.countryCallingCode}`,
      countryCode: data.country,
      currency: props.currency,
      email: data.email,
      firstName: data.firstName,
      // Hardcodes gender because it's required on BE, but we don't care
      gender: 'Male',
      generate_username: true,
      language: props.language,
      lastName: data.lastName,
      mobile: R.equals(R.head(data.phonenumber), '0')
        ? R.drop(1, data.phonenumber)
        : data.phonenumber,
      netrefererBTag: props.btag,
      password: data.password,
      passwordConfirmation: data.password,
      phoneVerificationCode: data.phoneVerificationCode,
      street: data.street,
      utmCampaign: props.utmCampaign,
      utmMedium: props.utmMedium,
      utmSource: props.utmSource,
      zip: data.zip,
    }

    props.onSubmit(userData, setErrors)
  }

  function handlePhoneValidation(data) {
    const userData = {
      countryCallingCode: `+${data.countryCallingCode}`,
      phone: R.equals(R.head(data.phonenumber), '0')
        ? R.drop(1, data.phonenumber)
        : data.phonenumber,
      language: props.language,
    }

    function failure(errors) {
      setErrors(errors)
      setCodeSentTo('')
    }

    function success() {
      setErrors([])
      setStep(RegistrationStep.Identity)
      setCodeSentTo(`${data.countryCallingCode}${data.phonenumber}`)
    }

    function validationSuccess() {
      if (props.shouldVerifyPhone) {
        props.onPhoneVerificationCodeRequest(userData, failure, success)
      } else {
        success()
      }
    }

    props.onPhoneValidation(userData, failure, validationSuccess)
  }

  function validatePassword(data) {
    if (data.email.toLowerCase().includes(data.password.toLowerCase())) {
      return setErrors(['password-matching-email'])
    }

    handlePhoneValidation(data)
  }

  React.useEffect(() => {
    if (!countries || countries.length === 0) {
      dispatch(Countries.fetchAll())
    }
  }, [countries])

  return (
    <Forms.Provider
      name="register"
      schema={schema}
      onSubmit={(formErrors, data) => {
        if (R.isEmpty(formErrors) && step > RegistrationStep.Credentials) {
          handleSubmit(data)
        }

        if (
          R.not(
            R.equals(
              codeSentTo,
              `${data.countryCallingCode}${data.phonenumber}`
            )
          )
        ) {
          props.onRegistrationStarted()
          validatePassword(data)
        }

        if (
          data.email &&
          data.password &&
          data.phonenumber &&
          data.countryCallingCode &&
          codeSentTo &&
          R.equals(codeSentTo, `${data.countryCallingCode}${data.phonenumber}`)
        ) {
          setStep(RegistrationStep.Identity)
        }
      }}
    >
      <QueryDrawer
        onSecondaryAction={
          step > RegistrationStep.Credentials
            ? () => setStep(RegistrationStep.Credentials)
            : null
        }
        activeQueryName="register"
        title={<Logotype variant="dark" height="36" />}
      >
        <Common.Box pb="80px" color="inputTitle" px={1}>
          <Common.Box
            fontSize="24px"
            fontWeight="700"
            fontFamily="head"
            color="black"
            pt="40px"
            pb="16px"
            textAlign="center"
          >
            {i18n.translate('register.title')}
          </Common.Box>
          <ProgressBar step={step} steps={2} />
          <RegistrationForm errors={errors} step={step} />
          <Common.Box
            pt={2}
            display="flex"
            fontSize="16px"
            justifyContent="center"
            textAlign="center"
            fontFamily="head"
            fontWeight="600"
          >
            {i18n.translate('register.already-a-member')}
            <Common.Link to="/login">
              <Common.Box
                color="simpleorange"
                pl="4px"
                style={{ textDecoration: 'underline' }}
              >
                {i18n.translate('register.log-in')}
              </Common.Box>
            </Common.Link>
          </Common.Box>
        </Common.Box>
      </QueryDrawer>
    </Forms.Provider>
  )
}

RegistrationDrawer.propTypes = {
  affiliateClickId: PropTypes.string,
  affiliateSubId: PropTypes.string,
  btag: PropTypes.string,
  clientType: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
  shouldVerifyPhone: PropTypes.bool,
  utmCampaign: PropTypes.string,
  utmMedium: PropTypes.string,
  utmSource: PropTypes.string,
  onPhoneVerificationCodeRequest: PropTypes.func.isRequired,
  onPhoneValidation: PropTypes.func.isRequired,
  onRegistrationStarted: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
}
